import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ExpenseService, Expense } from '../services/ExpenseService';
import { Plus, DollarSign, Calendar, Building, Eye, Edit, Trash2, Receipt, Clock, TrendingUp, Users, FileText, Wallet } from 'lucide-react';
import ModernLayout from '../components/ModernLayout';
import { StatCard, ActionCard, ModernCard } from '../components/ModernCards';
import WalletBalance from '../components/WalletBalance';

const DashboardPage: React.FC = () => {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadExpenses();
  }, []);

  const loadExpenses = async () => {
    try {
      const data = await ExpenseService.getExpenses();
      setExpenses(data);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to load expenses';
      setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
      console.error('Error loading expenses:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteExpense = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this expense?')) {
      try {
        await ExpenseService.deleteExpense(id);
        setExpenses(expenses.filter(expense => expense.id !== id));
      } catch (err: any) {
        const errorMessage = err.response?.data?.detail || err.message || 'Failed to delete expense';
        setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
      }
    }
  };

  const formatCurrency = (amount: number | string, currency: string) => {
    // Convert string to number if needed
    const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    
    // Handle NaN, null, or undefined amounts
    if (numericAmount === null || numericAmount === undefined || isNaN(numericAmount)) {
      return '$0.00';
    }
    
    // Map PYUSD to USD for display purposes since Intl.NumberFormat doesn't recognize PYUSD
    const displayCurrency = currency === 'PYUSD' ? 'USD' : currency;
    
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: displayCurrency,
      }).format(numericAmount);
    } catch (error) {
      // Fallback for any other invalid currency codes
      return `${currency} ${numericAmount.toFixed(2)}`;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'badge-info';
      case 'submitted':
        return 'badge-warning';
      case 'approved':
        return 'badge-success';
      case 'rejected':
        return 'badge-error';
      case 'sent_back':
        return 'badge-warning';
      case 'paid':
        return 'badge-paid';
      default:
        return 'badge-info';
    }
  };

  const totalAmount = expenses.reduce((sum, expense) => {
    // Use usd_amount if it exists and is valid, otherwise use amount
    const amount = (expense.usd_amount && expense.usd_amount > 0) ? expense.usd_amount : expense.amount;
    // Convert string to number if needed
    const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return sum + (!isNaN(numericAmount) ? numericAmount : 0);
  }, 0);
  const pendingExpenses = expenses.filter(expense => expense.status === 'submitted').length;
  const approvedExpenses = expenses.filter(expense => expense.status === 'approved').length;

  return (
    <ModernLayout
      title="Dashboard"
      subtitle={`Welcome back, ${user?.first_name}! Manage your expenses and approvals.`}
      actions={
        <button
          onClick={() => navigate('/expenses/create-from-receipt')}
          className="btn-primary flex items-center space-x-2"
        >
          <Receipt className="h-5 w-5" />
          <span>Create Expense</span>
        </button>
      }
    >
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <StatCard
          title="Expenses Created"
          value={expenses.length}
          subtitle="Total expenses created"
          icon={<Receipt className="h-6 w-6 text-green-600" />}
          trend={{ value: 12.5, isPositive: true }}
        />
        <StatCard
          title="Pending Approval"
          value={pendingExpenses}
          subtitle="Awaiting review"
          icon={<Clock className="h-6 w-6 text-yellow-600" />}
        />
        <StatCard
          title="Total Amount"
          value={formatCurrency(totalAmount, 'USD')}
          subtitle="From all receipts"
          icon={<DollarSign className="h-6 w-6 text-green-600" />}
          trend={{ value: 8.2, isPositive: true }}
        />
      </div>

      {/* Wallet Balance Section */}
      {user?.wallet_address && (
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Wallet className="h-6 w-6 text-green-600" />
            <h2 className="text-xl font-semibold text-gray-900">Your Wallet</h2>
          </div>
          <WalletBalance 
            walletAddress={user.wallet_address}
            showTitle={false}
          />
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <ActionCard
          title="Create Expense"
          description="Create expense from receipt image using AI"
          icon={<Receipt className="h-8 w-8 text-white" />}
          onClick={() => navigate('/expenses/create-from-receipt')}
        />
        <ActionCard
          title="Pending Approvals"
          description="Review and approve expenses"
          icon={<Users className="h-8 w-8 text-white" />}
          onClick={() => navigate('/pending-tasks')}
        />
      </div>

      {/* Recent Expenses */}
      <ModernCard className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-900">Recent Expenses</h3>
          <button
            onClick={() => navigate('/expenses')}
            className="text-green-600 hover:text-green-700 font-medium"
          >
            View All
          </button>
        </div>

        {isLoading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="loading-skeleton h-16 rounded-lg" />
            ))}
          </div>
        ) : expenses.length === 0 ? (
          <div className="text-center py-12">
            <Receipt className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No expenses created yet</h3>
            <p className="text-gray-600 mb-6">Get started by creating your first expense from a receipt.</p>
            <button
              onClick={() => navigate('/expenses/create-from-receipt')}
              className="btn-primary flex items-center space-x-2 mx-auto"
            >
              <Receipt className="h-5 w-5" />
              <span>Create Expense</span>
            </button>
          </div>
        ) : (
          <div className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Expense
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {expenses.slice(0, 10).map((expense) => (
                    <tr key={expense.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {expense.title}
                          </div>
                          <div className="text-sm text-gray-500">
                            {expense.vendor || 'No vendor'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900">
                          {formatCurrency((expense.usd_amount && expense.usd_amount > 0) ? expense.usd_amount : expense.amount, expense.currency)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(expense.status)}`}>
                          {expense.status.charAt(0).toUpperCase() + expense.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(expense.time)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => navigate(`/expenses/${expense.id}`)}
                            className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50"
                            title="View details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => navigate(`/expenses/${expense.id}/edit`)}
                            className="text-gray-600 hover:text-gray-900 p-1 rounded hover:bg-gray-50"
                            title="Edit expense"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteExpense(expense.id)}
                            className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                            title="Delete expense"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </ModernCard>
    </ModernLayout>
  );
};

export default DashboardPage;